import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TerraceClosureImpactDialog } from "@/components/TerraceClosureImpactDialog";
import { getUpcomingTerraceReservations } from "@/lib/terraceReservationUtils";
import type { Reservation, Table, CombinedTable, TableAvailability, TableWeekdayAvailability, TableBookablePeriod } from "@/types/table";

interface Props {
  establishmentId: string;
  terraceEnabled: boolean;
  reservations: Reservation[];
  tables: Table[];
  combinedTables: CombinedTable[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  bookablePeriods: TableBookablePeriod[];
  childrenCapacityMode: 'full' | 'half' | 'none';
  durationMinutes: number;
  customAssignmentOrderEnabled?: boolean;
}

/**
 * Terrasse désactivée : signale les réservations à venir encore sur la terrasse
 * et permet de les replacer en intérieur. Rien n'est déplacé automatiquement.
 */
export function TerraceReplaceBanner(props: Props) {
  const { terraceEnabled, reservations, tables, combinedTables } = props;
  const [open, setOpen] = useState(false);

  const upcoming = useMemo(() => {
    if (terraceEnabled) return [];
    const tIds = new Set(tables.filter(t => t.isTerrace).map(t => t.id));
    const cIds = new Set(combinedTables.filter(c => c.isTerrace).map(c => c.id));
    if (tIds.size === 0 && cIds.size === 0) return [];
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return getUpcomingTerraceReservations(reservations, tIds, cIds, today)
      .sort((a, b) => +new Date(a.date) - +new Date(b.date) || a.startTime.localeCompare(b.startTime));
  }, [terraceEnabled, reservations, tables, combinedTables]);

  // Liste figée à l'ouverture pour que les lignes réassignées restent visibles.
  const [snapshot, setSnapshot] = useState<Reservation[]>([]);

  if (upcoming.length === 0 && !open) return null;

  return (
    <>
      {upcoming.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between p-3 rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800 text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-orange-600" />
            <span>
              La terrasse est désactivée, mais {upcoming.length} réservation{upcoming.length > 1 ? 's' : ''} à venir {upcoming.length > 1 ? 'sont' : 'est'} encore sur la terrasse.
            </span>
          </div>
          <Button size="sm" variant="outline" className="w-full sm:w-auto shrink-0" onClick={() => { setSnapshot(upcoming); setOpen(true); }}>
            Voir et replacer
          </Button>
        </div>
      )}
      <TerraceClosureImpactDialog
        mode="replace"
        open={open}
        onOpenChange={setOpen}
        date={new Date().toISOString()}
        timeSlotId={null}
        reservations={snapshot}
        establishmentId={props.establishmentId}
        tables={tables}
        combinedTables={combinedTables}
        allReservations={reservations}
        availabilities={props.availabilities}
        weekdayAvailabilities={props.weekdayAvailabilities}
        bookablePeriods={props.bookablePeriods}
        childrenCapacityMode={props.childrenCapacityMode}
        durationMinutes={props.durationMinutes}
        customAssignmentOrderEnabled={props.customAssignmentOrderEnabled}
        onConfirm={() => setOpen(false)}
      />
    </>
  );
}
